import React from 'react'

import { Delayed } from '../../../../components/delayed'
import { Spinner } from '../../../../components/spinner'
import { WIDGET_BOX_STYLES } from '../constants'
import { useETFHoldingsQuery, useRefetchData } from '../hooks'
import {
  ETFHoldingDataType,
  ETFHoldingsBreakdownResponse,
  ETFHoldingsBreakdownType,
  ETFHoldingsQuery,
  ETFHoldingsWidgetNavigation,
} from '../types'
import { getNormalizedBreakdownData } from '../utils'
import { Box } from './Box'
import { BreakdownMap } from './BreakdownMap'
import { Header } from './Header'
import { PieChart } from './Pie'

type BreakdownViewProps = {
  query: ReturnType<typeof useETFHoldingsQuery>
}

const VIEW_SETTINGS: ETFHoldingsWidgetNavigation = {
  navType: ETFHoldingsQuery.Breakdown,
  title: 'Holdings Breakdown',
  viewTypes: [
    { icon: 'chartPie', chartType: ETFHoldingsBreakdownType.PieChart },
    { icon: 'chartTreemap', chartType: ETFHoldingsBreakdownType.Map },
  ],
}

function getChartComponent({
  type,
  data,
  dataTimestamp,
}: {
  type: ETFHoldingsBreakdownType
  data: ETFHoldingsBreakdownResponse | null
  dataTimestamp: number
}) {
  if (!data || data.rowData.length === 0)
    return (
      <Delayed delayComponent={<div className="h-full w-full" />}>
        <div className="flex h-full w-full items-center justify-center">
          <Spinner />
        </div>
      </Delayed>
    )
  return type === ETFHoldingsBreakdownType.Map ? (
    <BreakdownMap type={ETFHoldingsBreakdownType.Map} data={data} dataTimestamp={dataTimestamp} />
  ) : (
    <PieChart type={ETFHoldingsBreakdownType.PieChart} data={data} />
  )
}

export function BreakdownView({ query }: BreakdownViewProps) {
  const type = query.bd
  const fetchedData = useRefetchData(query.ticker, ETFHoldingDataType.Breakdown)
  const data = React.useMemo(() => getNormalizedBreakdownData(fetchedData.data), [fetchedData.data])

  return (
    <Box key={VIEW_SETTINGS.navType} className={WIDGET_BOX_STYLES}>
      <div className="flex shrink grow flex-col">
        <Header query={query} widgetType={VIEW_SETTINGS} />
        {getChartComponent({ type, data, dataTimestamp: fetchedData.dataUpdatedAt })}
      </div>
    </Box>
  )
}
